import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../pages/SHL-03/SHL-03_system-states.css';
import '../pages/SHL-04/SHL-04_licence-states.css';
import { t } from '../i18n';
import { api, ApiError, call } from '../api/client';
import { PAGES, pageTitle, sectionName, type PageDef } from './pages';
import type { Session } from './session';
import { can } from './session';
import { AppFrame } from './AppFrame';
import { date, commas } from '../lib/format';
import { roleName } from '../lib/names';
import { rich } from '../lib/rich';

/** Full-page "You don't have access to this page" (SHL-03), inside the shell. */
export function NoAccessPage({ page, session }: { page: PageDef; session: Session | null | undefined }) {
  const role = commas((session?.roles || []).map(roleName)) || '—';
  const title = pageTitle(page);
  const open = PAGES.filter((p) => p.shell && p.nav && p.perm && can(session, p.perm) && p.id !== page.id)
    .filter((p, i, a) => a.findIndex((x) => x.nav === p.nav) === i)
    .slice(0, 10);
  return (
    <main className="bx-main">
      <div className="bx-crumbs">
        <span>{sectionName(page.section)}</span>
        <span aria-hidden="true">{'/'}</span>
        <span className="cur">{title}</span>
      </div>
      <div className="shl3-full">
        <section className="shl3-card" aria-labelledby="na-t">
          <span className="bx-state-ic shl3-big">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M7 11V8a5 5 0 0 1 10 0v3M5 11h14v10H5zM12 15v2" />
            </svg>
          </span>
          <h1 className="bx-h1" id="na-t">
            {t('app.noaccess.title', "You don't have access to this page")}
          </h1>
          <p className="shl3-lead">
            {t(
              'app.noaccess.lead',
              '{page} is not part of your role. Ask an Owner to add it to your role, or to move you to a role that includes it.',
              { page: title },
            )}
          </p>
          <dl className="shl3-facts">
            <div>
              <dt>{t('app.noaccess.page', 'Page')}</dt>
              <dd>
                {title} <span className="bx-badge bx-badge--mono">{page.id}</span>
              </dd>
            </div>
            <div>
              <dt>{t('app.noaccess.role', 'Your role')}</dt>
              <dd>
                {role} · {session?.user.name}
              </dd>
            </div>
            <div>
              <dt>{t('app.noaccess.who', 'Who can grant access')}</dt>
              <dd>{t('app.noaccess.owners', 'An Owner of this instance (Settings › Roles)')}</dd>
            </div>
          </dl>
          <div className="bx-hstack" style={{ justifyContent: 'center' }}>
            <a className="bx-btn bx-btn--primary" href="/dashboard">
              {t('app.noaccess.dashboard', 'Go to dashboard')}
            </a>
          </div>
        </section>
        {open.length ? (
          <div className="shl3-also">
            <span className="bx-eb">{t('app.noaccess.can-open', 'Pages your role can open')}</span>
            <div className="bx-hstack" style={{ justifyContent: 'center', gap: '6px' }}>
              {open.map((p) => (
                <a key={p.id} className="bx-chip" href={'/' + p.slug}>
                  {pageTitle(p)}
                </a>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </main>
  );
}

/** Full-page 404 (SHL-03) with search, inside the shell. */
export function NotFoundPage({ path }: { path: string }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  return (
    <main className="bx-main">
      <div className="shl3-full">
        <section className="shl3-card" aria-labelledby="nf-t">
          <span className="shl3-404" aria-hidden="true">
            {'404'}
          </span>
          <h1 className="bx-h1" id="nf-t">
            {t('app.notfound.title', 'Page not found')}
          </h1>
          <p className="shl3-lead">
            {rich((m) =>
              t(
                'app.notfound.lead',
                'Nothing lives at {path}. The link may be mistyped, or the item was removed or merged into another one.',
                {
                  path: m(
                    <span className="bx-m bx-ink" dir="ltr">
                      {path}
                    </span>,
                  ),
                },
              ),
            )}
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              window.dispatchEvent(new CustomEvent('bx:search', { detail: q }));
            }}
            style={{ width: '100%' }}
          >
            <label className="bx-search shl3-search">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-4-4" />
              </svg>
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('app.notfound.search', 'Search login, name, symbol, group or case')}
                aria-label={t('app.notfound.search-label', 'Search Brainux')}
              />
            </label>
          </form>
          <div className="bx-hstack" style={{ justifyContent: 'center' }}>
            <button type="button" className="bx-btn" onClick={() => navigate(-1)}>
              {t('app.notfound.back', 'Go back')}
            </button>
            <a className="bx-btn bx-btn--primary" href="/dashboard">
              {t('app.notfound.dashboard', 'Go to dashboard')}
            </a>
          </div>
        </section>
      </div>
    </main>
  );
}

/**
 * Locked instance (SHL-04, no shell): only the Launcher can sign in and enter a new licence key.
 * Signed in as the Launcher, the key form activates it (`PUT /licence/key`).
 */
export function LockedScreen({
  validUntil,
  session,
  onUnlocked,
}: {
  validUntil: string | null;
  session: Session | null | undefined;
  onUnlocked: () => void;
}) {
  const [key, setKey] = useState('');
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const launcher = !!session?.user.is_launcher;
  const ended = validUntil ? date(validUntil, undefined, false) : '—';
  const activate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/\S{8,}/.test(key)) {
      setErr(t('app.locked.key-required', 'Paste the key Dux FX sent with your renewal.'));
      return;
    }
    try {
      await call(api.PUT('/licence/key', { body: { key } as any }));
      setDone(true);
      setTimeout(onUnlocked, 1500);
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : String(x));
    }
  };
  return (
    <AppFrame className="shl4-lockapp">
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <svg
          width="30"
          height="30"
          viewBox="0 0 24 24"
          fill="none"
          style={{ stroke: 'var(--gold)' }}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 4a4 4 0 0 0-4 4 4 4 0 0 0-3 6.5A4 4 0 0 0 9 20h3V4Z" />
          <path d="M12 4a4 4 0 0 1 4 4 4 4 0 0 1 3 6.5A4 4 0 0 1 15 20h-3" />
          <path d="M8.5 11h3M12.5 14h3" />
        </svg>
        <span style={{ fontSize: '20px', fontWeight: '600' }}>{t('app.brand', 'Brainux')}</span>
        <span className="bx-by">{t('app.by-dux-fx', 'BY DUX FX')}</span>
      </div>
      <main className="shl4-lock" aria-labelledby="lk-t">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
          <span className="shl4-lock-ic">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M7 11V8a5 5 0 0 1 10 0v3M5 11h14v10H5zM12 15v2" />
            </svg>
          </span>
          <div style={{ minWidth: '0' }}>
            <h1 id="lk-t" style={{ fontSize: '22px', lineHeight: '28px', fontWeight: '600', letterSpacing: '-.01em' }}>
              {t('app.locked.title', 'Brainux is locked')}
            </h1>
            <p className="bx-c" style={{ marginTop: '4px', fontSize: '13px', lineHeight: '19px', color: 'var(--ink-2)' }}>
              {t(
                'app.locked.lead',
                'The licence ended on {date}. After the grace and read-only periods this instance locked. Your data is still on this server; nothing was deleted.',
                { date: ended },
              )}
            </p>
          </div>
        </div>
        {launcher && !done ? (
          <form style={{ display: 'flex', flexDirection: 'column', gap: '14px' }} onSubmit={activate}>
            <div className="bx-field">
              <label className="bx-label" htmlFor="lk-key">
                {t('app.locked.new-key', 'New licence key')}
              </label>
              <input
                id="lk-key"
                className="bx-input bx-input--mono"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="BXL-XXXX-XXXX-XXXX-XXXX"
                autoComplete="off"
                spellCheck="false"
                aria-invalid={err ? 'true' : 'false'}
              />
              {err ? <span className="bx-err">{err}</span> : null}
            </div>
            <button type="submit" className="bx-btn bx-btn--primary bx-btn--block" style={{ minHeight: '40px' }}>
              {t('app.locked.activate', 'Activate and unlock')}
            </button>
          </form>
        ) : null}
        {!launcher ? (
          <>
            <a className="bx-btn bx-btn--block" href="/login" style={{ minHeight: '40px' }}>
              {t('app.locked.sign-in', 'Sign in as Launcher')}
            </a>
            <p className="bx-cap" style={{ textAlign: 'center' }}>
              {t('app.locked.only-launcher', 'Only the Launcher account can sign in while Brainux is locked.')}
            </p>
          </>
        ) : null}
        {done ? (
          <div className="bx-notice" role="status">
            <span className="bx-spinner" style={{ marginTop: '1px' }} />
            <span>
              <b>{t('app.locked.accepted', 'Key accepted.')}</b> {t('app.locked.restarting', 'Brainux opens in a moment.')}
            </span>
          </div>
        ) : null}
      </main>
    </AppFrame>
  );
}
